// Global font options available in Admin → Settings → Global Font.
// Weights include the range used across headings, body and buttons.
// All fonts below support Latin Extended (Latvian diacritics: ā č ē ģ ī ķ ļ ņ š ū ž).

export type FontOption = {
  id: string;
  label: string;
  family: string;      // CSS family name, unquoted
  googleName: string;  // Google Fonts family param (spaces → +)
  weights: string;     // e.g. "400;500;600;700"
  category: "sans-serif";
};

export const FONT_OPTIONS: FontOption[] = [
  { id: "default",           label: "Default (Space Grotesk + Inter)", family: "Inter",              googleName: "Inter",              weights: "400;500;600;700", category: "sans-serif" },
  { id: "figtree",           label: "Figtree",            family: "Figtree",            googleName: "Figtree",            weights: "400;500;600;700;800", category: "sans-serif" },
  { id: "inter",             label: "Inter",              family: "Inter",              googleName: "Inter",              weights: "400;500;600;700", category: "sans-serif" },
  { id: "manrope",           label: "Manrope",            family: "Manrope",            googleName: "Manrope",            weights: "400;500;600;700;800", category: "sans-serif" },
  { id: "montserrat",        label: "Montserrat",         family: "Montserrat",         googleName: "Montserrat",         weights: "400;500;600;700", category: "sans-serif" },
  { id: "poppins",           label: "Poppins",            family: "Poppins",            googleName: "Poppins",            weights: "400;500;600;700", category: "sans-serif" },
  { id: "plus-jakarta-sans", label: "Plus Jakarta Sans",  family: "Plus Jakarta Sans",  googleName: "Plus+Jakarta+Sans",  weights: "400;500;600;700;800", category: "sans-serif" },
];

export const DEFAULT_FONT_ID = "default";

export function getFontById(id: string | undefined | null): FontOption {
  return FONT_OPTIONS.find((f) => f.id === id) || FONT_OPTIONS[0];
}

const LINK_ID = "global-font-link";

/**
 * Apply a font globally by injecting a Google Fonts <link> and updating
 * the --font-body / --font-display CSS variables on :root. Safe to call
 * repeatedly — the previous link tag is replaced.
 */
export function applyGlobalFont(id: string) {
  const font = getFontById(id);

  // Default preserves the original design system fonts already preloaded in index.html.
  if (font.id === "default") {
    const existing = document.getElementById(LINK_ID);
    if (existing) existing.remove();
    document.documentElement.style.setProperty("--font-body", `'Inter', sans-serif`);
    document.documentElement.style.setProperty("--font-display", `'Space Grotesk', sans-serif`);
    return;
  }

  const href = `https://fonts.googleapis.com/css2?family=${font.googleName}:wght@${font.weights}&display=swap`;
  let link = document.getElementById(LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.id = LINK_ID;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;

  const stack = `'${font.family}', system-ui, -apple-system, Segoe UI, sans-serif`;
  document.documentElement.style.setProperty("--font-body", stack);
  document.documentElement.style.setProperty("--font-display", stack);
}
